import { db } from '@backend/db/db'
import { functions } from '@backend/functions/functions'
import { ArrowBackIcon, RepeatIcon } from '@chakra-ui/icons'
import { Badge, Box, Center, Checkbox, Flex, Spinner, Text, VStack } from '@chakra-ui/react'
import { usePopUpMessage } from '@hb/react'
import {
  ASSESSMENT_STAGE_REPORT_COLUMN_LABELS,
  ASSESSMENT_STAGE_REPORT_COLUMNS,
  AssessmentStage,
  AssessmentStageReport,
  AssessmentStageReportColumn,
  DEFAULT_ASSESSMENT_STAGE_REPORT_COLUMNS,
  DownloadPregnancyStageReportArgs,
  GeneratePregnancyStageReportArgs,
  MIDWIVES,
  PRACTICE_STAGE_REPORTS,
  WithId,
} from '@hb/shared'
import { collection, CollectionReference, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import React, { useCallback, useMemo, useState } from 'react'
import { useQuery } from '../../../hooks/backend/useQuery'
import { ActionButton, SolidActionButton } from '../../Buttons'
import { ColumnSelector } from './ColumnSelector'
import { ToggleSelectGroup } from './ToggleSelectGroup'

const ALL_STAGES: AssessmentStage[] = [
  'Inquiry',
  'Authorization',
  'Consent Forms',
  'Send Claim',
  'Claims',
  'Complaints',
  'Complete',
]

const generateReportFn = httpsCallable<GeneratePregnancyStageReportArgs, { reportId: string }>(
  functions,
  'generatePregnancyReport',
)

const downloadReportFn = httpsCallable<DownloadPregnancyStageReportArgs, { signedUrl: string }>(
  functions,
  'downloadPregnancyStageReport',
)

const ReportRow = ({ report }: { report: WithId<AssessmentStageReport> }) => {
  const [downloading, setDownloading] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const { showError, showMessage } = usePopUpMessage()

  const handleDownload = useCallback(async () => {
    setDownloading(true)
    try {
      const result = await downloadReportFn({ reportId: report.id, practiceId: report.practiceId })
      window.open(result.data.signedUrl, '_blank')
    } catch (e: any) {
      showError(e?.message ?? 'Error downloading report')
    } finally {
      setDownloading(false)
    }
  }, [report.id, report.practiceId, showError])

  const handleRegenerate = useCallback(async () => {
    setRegenerating(true)
    try {
      await generateReportFn({
        practiceId: report.practiceId,
        stages: report.stages,
        columns: report.columns,
        includeArchived: report.includeArchived,
      })
      showMessage({ type: 'success', text: 'Report is being generated' })
    } catch (e: any) {
      showError(e?.message ?? 'Error generating report')
    } finally {
      setRegenerating(false)
    }
  }, [
    report.practiceId,
    report.stages,
    report.columns,
    report.includeArchived,
    showError,
    showMessage,
  ])

  const date = useMemo(() => new Date(report.createdOn).toLocaleString(), [report.createdOn])
  const columnLabels = useMemo(
    () => report.columns.map(c => ASSESSMENT_STAGE_REPORT_COLUMN_LABELS[c]).join(', '),
    [report.columns],
  )

  return (
    <Box w="100%" borderWidth={1} borderRadius="md" p={3} bg="white" _hover={{ bg: 'gray.50' }}>
      <Flex justify="space-between" align="flex-start" gap={3}>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            {date}
          </Text>
          <Flex wrap="wrap" gap={1} mb={2}>
            {report.stages.map(stage => (
              <Badge key={stage} colorScheme="purple" fontSize="xs">
                {stage}
              </Badge>
            ))}
            {report.includeArchived && (
              <Badge colorScheme="orange" fontSize="xs">
                Incl. archived
              </Badge>
            )}
          </Flex>
          <Text fontSize="xs" color="gray.600" noOfLines={2}>
            {columnLabels}
          </Text>
        </Box>
        <Flex flexShrink={0} align="center" gap={2}>
          {report.status === 'generating' && (
            <Flex align="center" gap={2}>
              <Spinner size="sm" color="blue.400" />
              <Text fontSize="sm" color="blue.500">
                Generating…
              </Text>
            </Flex>
          )}
          {report.status === 'error_generating' && (
            <Text fontSize="sm" color="red.500">
              Error
            </Text>
          )}
          <Flex gap={2} flexFlow="column">
            {report.status === 'generated' && (
              <SolidActionButton size="sm" isLoading={downloading} onClick={handleDownload}>
                Download
              </SolidActionButton>
            )}
            {report.status !== 'generating' && (
              <ActionButton
                size="sm"
                leftIcon={<RepeatIcon />}
                isLoading={regenerating}
                onClick={handleRegenerate}>
                Regenerate
              </ActionButton>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  )
}

const ListView = ({ practiceId, onCreateNew }: { practiceId: string; onCreateNew: () => void }) => {
  const reportsRef = useMemo(
    () =>
      query(
        collection(
          db,
          MIDWIVES,
          practiceId,
          PRACTICE_STAGE_REPORTS,
        ) as CollectionReference<AssessmentStageReport>,
        orderBy('createdOn', 'desc'),
      ),
    [practiceId],
  )

  const { dataArr: reports, loading } = useQuery<AssessmentStageReport>(reportsRef)

  return (
    <Flex direction="column" h="100%">
      <Flex
        bg="gray.50"
        _dark={{ bg: 'gray.800' }}
        align="center"
        gap={2}
        px={4}
        py={3}
        borderBottomWidth={1}
        w="100%">
        <Text fontWeight="600" color="gray.700" _dark={{ color: 'gray.200' }} flex={1} minW={0}>
          Pregnancy Stage Reports
        </Text>
        <SolidActionButton size="sm" onClick={onCreateNew}>
          Create New Report
        </SolidActionButton>
      </Flex>
      <Box flex={1} overflowY="auto" p={4}>
        {loading && (
          <Center py={8}>
            <Spinner color="blue.400" />
          </Center>
        )}
        {!loading && (!reports || reports.length === 0) && (
          <Center py={8}>
            <Text color="gray.400">No reports yet. Create one to get started.</Text>
          </Center>
        )}
        {reports && reports.length > 0 && (
          <VStack spacing={2} align="stretch">
            {reports.map(report => (
              <ReportRow key={report.id} report={report} />
            ))}
          </VStack>
        )}
      </Box>
    </Flex>
  )
}

const CreateView = ({ practiceId, onBack }: { practiceId: string; onBack: () => void }) => {
  const [selectedStages, setSelectedStages] = useState<AssessmentStage[]>([])
  const [selectedColumns, setSelectedColumns] = useState<AssessmentStageReportColumn[]>([
    ...DEFAULT_ASSESSMENT_STAGE_REPORT_COLUMNS,
  ])
  const [includeArchived, setIncludeArchived] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { showError, showMessage } = usePopUpMessage()

  const toggleStage = useCallback((stage: AssessmentStage) => {
    setSelectedStages(prev =>
      prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage],
    )
  }, [])

  const allStagesSelected = selectedStages.length === ALL_STAGES.length

  const toggleAllStages = useCallback(() => {
    setSelectedStages(allStagesSelected ? [] : [...ALL_STAGES])
  }, [allStagesSelected])

  const handleSubmit = useCallback(async () => {
    if (!selectedStages.length) {
      showError('Select at least one stage')
      return
    }
    if (!selectedColumns.length) {
      showError('Select at least one column')
      return
    }
    setSubmitting(true)
    try {
      await generateReportFn({
        practiceId,
        stages: selectedStages,
        columns: selectedColumns,
        includeArchived,
      })
      showMessage({ type: 'success', text: 'Report is being generated' })
      onBack()
    } catch (e: any) {
      showError(e?.message ?? 'Error generating report')
    } finally {
      setSubmitting(false)
    }
  }, [practiceId, selectedStages, selectedColumns, includeArchived, onBack, showError, showMessage])

  return (
    <Flex direction="column" h="100%">
      <Flex
        w="100%"
        gap={3}
        align="center"
        px={4}
        py={3}
        borderBottomWidth={1}
        bg="gray.50"
        _dark={{ bg: 'gray.800' }}>
        <Text flex={1} minW={0} fontWeight="600" color="gray.700">
          New Pregnancy Report
        </Text>
        <ActionButton leftIcon={<ArrowBackIcon />} size="sm" onClick={onBack}>
          Back to Reports
        </ActionButton>
      </Flex>
      <Box flex={1} overflowY="auto" p={4}>
        <VStack spacing={3} align="stretch">
          <ToggleSelectGroup
            title="Stages"
            options={ALL_STAGES}
            getLabel={stage => stage}
            selected={selectedStages}
            onToggle={toggleStage}
            onToggleAll={toggleAllStages}
          />
          <Checkbox
            colorScheme="green"
            isChecked={includeArchived}
            onChange={e => setIncludeArchived(e.target.checked)}>
            <Text fontSize="sm" color="gray.700">
              Include archived pregnancies
            </Text>
          </Checkbox>
          <ColumnSelector
            title="Columns"
            options={ASSESSMENT_STAGE_REPORT_COLUMNS}
            getLabel={col => ASSESSMENT_STAGE_REPORT_COLUMN_LABELS[col]}
            selected={selectedColumns}
            onChange={setSelectedColumns}
          />
        </VStack>
      </Box>
      <Flex
        bg="gray.50"
        _dark={{ bg: 'gray.800' }}
        px={4}
        py={3}
        borderTopWidth={1}
        justify="flex-end">
        <SolidActionButton
          isLoading={submitting}
          isDisabled={!selectedStages.length || !selectedColumns.length}
          onClick={handleSubmit}>
          Generate Report
        </SolidActionButton>
      </Flex>
    </Flex>
  )
}

export const PregnanciesTabContent: React.FC<{ practiceId: string }> = ({ practiceId }) => {
  const [view, setView] = useState<'list' | 'create'>('list')
  return view === 'list' ? (
    <ListView practiceId={practiceId} onCreateNew={() => setView('create')} />
  ) : (
    <CreateView practiceId={practiceId} onBack={() => setView('list')} />
  )
}
